import { Exception } from '@ch-types/Exception';

import _ from 'lodash';

const enum Status {
    FULFILLED = 'fulfilled',
    REJECTED = 'rejected',
}

interface ITimeoutInfoType {
    durationMillis: number;
    message?: string;
    error?: string | Error | Exception;
}

interface IPartitionType<T> {
    fulfilled: Array<PromiseSettledResult<T>>;
    rejected: Array<PromiseSettledResult<T>>;
}

export class PromiseUtils {
    /**
     * Delay function returns a Promise delayed for timeout ms.
     */
    public static delay(timeout: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, timeout));
    }

    /**
     * Returns a new resolved promise.
     */
    public static newResolvedPromise<T>(data: T): Promise<T> {
        return Promise.resolve(data);
    }

    /**
     * Returns a new rejected promise.
     */
    public static newRejectedPromise<T>(data: T): Promise<T> {
        return Promise.reject(data);
    }

    /**
     * Returns a new rejected promise with an error message.
     */
    public static newRejectedPromiseWithErrorMessage(errorMsg: string): Promise<string> {
        return Promise.reject(
            new Exception({
                message: errorMsg,
            }),
        );
    }

    public static newRejectedPromiseWithErrorCode(errorCode: number): Promise<number> {
        return Promise.reject({
            code: errorCode,
        });
    }

    /**
     * An extension of allSettled that partitions the fulfilled and rejected promises.
     *
     * @resolve with an object containing fulfilled and rejected promises.
     */
    public static async allSettledPartitioned<T>(localPromises: Array<Promise<T>>): Promise<IPartitionType<T>> {
        const results: Array<PromiseSettledResult<T>> = await Promise.allSettled(localPromises);
        const partitioned = _.partition(results, (result) => {
            return result.status === Status.FULFILLED;
        });

        return {
            fulfilled: partitioned[0],
            rejected: partitioned[1],
        };
    }

    /**
     * Executes a promise or times out if the timeout expires (default 5 seconds).
     * If timeout received is <= 0 , executes promise without timeout.
     *
     *
     * @param promise
     *              the promise to execute
     * @param timeoutInfo
     *              {
     *                  durationMillis: should timeout after this duration
     *                  message: should reject with this message in the event of a timeout
     *              }
     */
    public static async executeWithTimeout<T>(promise: Promise<T>, timeoutInfo?: ITimeoutInfoType): Promise<T> {
        timeoutInfo = timeoutInfo || { durationMillis: 5000 };
        const durationMillis: number = timeoutInfo.durationMillis;
        const timeoutMessage: string = timeoutInfo.message || `Could not execute a promise within ${durationMillis} ms`;
        const timeoutError = timeoutInfo.error || timeoutMessage;

        if (durationMillis > 0) {
            const timeoutPromise = new Promise<T>((resolve, reject) => setTimeout(() => reject(timeoutError), durationMillis));
            try {
                await Promise.race([timeoutPromise, promise]);
                return promise;
            } catch (rejectedData) {
                return Promise.reject(rejectedData);
            }
        } else {
            return promise;
        }
    }

    /**
     * Retry mechanism for promise rejections
     * @param  {Function} fn         Function to retry
     * @param  {Number}   delayTime         Retry delay in ms, default 1000ms
     * @param  {Number}   maxRetries Max time of retrying, default 4 attemps
     * @return {Promise}
     */
    public static async executeWithRetry<T>(fn: () => Promise<T>, delayTime?: number, maxRetries?: number): Promise<T> {
        if (delayTime === undefined) {
            delayTime = PromiseUtils.MAX_DELAY_TIME;
        }
        if (maxRetries === undefined) {
            maxRetries = PromiseUtils.MAX_EXECUTION_RETRIES;
        }

        return PromiseUtils.retryHelper(fn, delayTime, 1, maxRetries);
    }

    private static MAX_EXECUTION_RETRIES = 4;
    private static MAX_DELAY_TIME = 1000;

    /**
     * Helper function to recursively retry a rejected promise
     */
    private static async retryHelper<T>(fn: () => Promise<T>, ms: number, currentAttempt: number, maxRetries: number): Promise<T> {
        try {
            const resolvedData = await fn();
            return Promise.resolve(resolvedData);
        } catch (rejectedData) {
            if (currentAttempt === maxRetries) {
                console.error('Maximum retries exceeded. Failing');
                return Promise.reject(rejectedData);
            } else {
                await PromiseUtils.delay(ms);
                console.info(`Retrying failed promise..., current attempt: ${currentAttempt}`);
                return PromiseUtils.retryHelper(fn, ms, currentAttempt + 1, maxRetries);
            }
        }
    }
}
